import moment from 'moment';
import { formatToCurrency } from '@/lib/helpers';

export const generateInsuranceSummary = (data = {}) => {

  const borderColor = '#9ca3af';
  const bigMargin = 20;

  const accountHolders = () => {
    if (!data.loan?.loanDistributions) return '';
    return data.loan?.loanDistributions
      .map(
        (d) =>
          `${d.person?.firstName} ${d.person?.middleName} ${d.person?.lastName}`
      )
      .join(', ');
  };

  const holders = accountHolders();

  const tableLayout = {
    hLineColor: borderColor,
    vLineColor: borderColor,
    paddingBottom: () => 4,
    paddingTop: () => 4,
    paddingLeft: () => 8,
    paddingRight: () => 8
  };
  /**@type {import('pdfmake/interfaces').TDocumentDefinitions} */
  const dd = {
    footer: function (currentPage, pageCount) {
      return {
        text: `Page ${currentPage.toString()} of ${pageCount.toString()}`,
        alignment: 'center'
      };
    },
    info: {
      title: 'Insurance Summary', // name of the document being printed
      modDate: new Date(), // date the document was last modified
      creationDate: new Date(), // date the document was created
      keywords: 'insurance summary, statement of insurance, loan, finance', // keywords associated with the document
      author: 'Sodality Co-operative Credit Union Limited', // company name
      subject: 'Insurance Summary', // name of the document being printed
      creator: data.user?.username, // name of the logged in user
      producer: data.company?.name // company name
    },
    content: [
      data.company?.companyImage && {
        image: 'companyImage',
        fit: [80, 80]
      },
      {
        marginTop: 20,
        text: data.company?.name,
        fontSize: 22,
        bold: true
      },
      { text: 'Statement of Insurance', fontSize: 14 },
      { text: "(Borrower's Information)", alignment: 'right' },
      { text: accountHolders(), alignment: 'right', bold: true },
      {
        text: `Account #: ${data.loan?.mainAccountNumber}`,
        alignment: 'right'
      },
      {
        text: `Branch: ${data.loan?.accreditedBranch?.branchName || ''}`,
        alignment: 'right'
      },
      {
        text: `Loan Product: ${data.loan?.loanProduct || ''}`,
        alignment: 'right'
      },
      {
        marginTop: bigMargin * 2,
        marginBottom: bigMargin,
        layout: tableLayout,
        table: {
          headerRows: 1,
          body: [
            [
              { text: 'Policy No.', bold: false, alignment: 'center' },
              { text: 'Owner', bold: false, alignment: 'center' },
              { text: 'Insurance Name', bold: false, alignment: 'center' },
              { text: 'Coverage Amount', bold: false, alignment: 'center' },
              { text: 'Available Amount', bold: false, alignment: 'center' },
              { text: 'Pledged Amount', bold: false, alignment: 'center' },
              { text: 'Term', bold: false, alignment: 'center' },
              { text: 'Date Issued', bold: false, alignment: 'center' },
              { text: 'Expiry Date', bold: false, alignment: 'center' }
            ],
            ...data.insurances?.map((insurance) => {
              return [
                { text: insurance.policyNumber },
                {
                  text: insurance.insuranceToOwner
                    .map((o) => o.owners.ownerName)
                    .join(',')
                },
                { text: insurance.companyName },
                { text: formatToCurrency(insurance.coverageAmount) },
                { text: formatToCurrency(insurance.unusedAmount) },
                { text: formatToCurrency(insurance.amountPledgeInternal) },
                { text: insurance.term },
                { text: moment(insurance.dateCreated).format('MMM D, YYYY') },
                { text: moment(insurance.expirationDate).format('MMM D, YYYY') }
              ];
            })
          ]
        }
      },
      {
        marginTop: bigMargin,
        marginBottom: bigMargin / 2,
        text: 'Declaration of Member',
        style: 'tableHeading',
        fontSize: 14
      },
      {
        text: 'I hereby confirm that the information given herein is true in all respects, accurate and complete and that I have not withheld any information that might affect the Credit Union’s decision. I am agreeable to the Credit Union taking such steps, as it may deem necessary to verify any of the information given.'
      },
      { text: 'Signatures', marginTop: bigMargin, bold: true },
      // Map over the holders and display a signature line for each along with their name below
      holders &&
        holders.split(',').map((holder) => {
          return [
            {
              columns: [
                { text: '_______________________________', font: 'Roboto' },
                {
                  text: '_______________________________',
                  font: 'Roboto',
                  alignment: 'right'
                }
              ],
              marginTop: 15
            },
            {
              marginTop: 10,
              columns: [
                {
                  text: `Borrower: ${holder}`,
                  style: 'supSignature',
                  alignment: 'left'
                },
                {
                  text: 'Date Signed',
                  alignment: 'right',
                  style: 'supSignature'
                }
              ]
            }
          ];
        }),
      {
        marginTop: 15,
        columns: [
          { text: '_______________________________', font: 'Roboto' },
          {
            text: '_______________________________',
            font: 'Roboto',
            alignment: 'right'
          }
        ]
      },
      {
        marginTop: 10,
        columns: [
          {
            text: `Prepared by: ${data.user?.username}`,
            style: 'supSignature',
            alignment: 'left'
          },
          { text: 'Date Signed', alignment: 'right', style: 'supSignature' }
        ]
      }
    ],
    images: {
      companyImage: data.company?.companyImage
    },
    styles: {
      tableHeader: {
        bold: true
      },
      supSignature: {
        fontSize: 18,
        sup: true,
        alignment: 'center'
      },
      tableHeading: {
        alignment: 'left',
        fontSize: 16,
        bold: true
      }
    },
    defaultStyle: {
      font: 'Lora',
      color: '#111827',
      fontSize: 11,
      lineHeight: 1.3
    }
  };

  return dd;
};
